import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Icon from '../components/atoms/Icon';
import Button from '../components/atoms/Button';

const DataAnalysisDashboard = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [activeChartTab, setActiveChartTab] = useState(0);
  const [error, setError] = useState(null);

  // Hardcoded API key (later will be user-specific from database)
  const API_KEY = 'aptirix_data_analysis_2024_secure_key_v1';

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(fileExt)) {
        setError('Please upload a CSV or XLSX file');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/data-analysis/upload', {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      setAnalysisResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const renderChart = (chartConfig, index) => {
    const { chart_type, title, x_column, y_column, z_column, additional_config } = chartConfig;
    
    // Generate chart data based on chart type
    const generateChartData = () => {
      if (!analysisResult?.table_data?.rows || analysisResult.table_data.rows.length === 0) {
        console.warn('No data available for chart:', title);
        return null;
      }

      const rows = analysisResult.table_data.rows;
      
      // Helper to get valid column data
      const getColumnData = (colName) => {
        if (!colName || colName === 'count') return null;
        return rows.map(row => row[colName]).filter(val => val !== null && val !== undefined);
      };

      // Helper to get numeric column or first available
      const getValidColumn = (preferredCol) => {
        if (preferredCol && preferredCol !== 'count') return preferredCol;
        // Find first numeric column
        const numericCols = Object.keys(analysisResult.statistics?.numeric_columns || {});
        if (numericCols.length > 0) return numericCols[0];
        // Fallback to first column
        return analysisResult.table_data.columns[0];
      };

      const validXCol = getValidColumn(x_column);
      const validYCol = y_column === 'count' ? 'count' : getValidColumn(y_column);
      
      // BAR / COLUMN CHARTS
      if (chart_type === 'bar' || chart_type === 'column') {
        const categories = {};
        rows.forEach(row => {
          const key = String(row[validXCol] || 'Unknown');
          if (validYCol === 'count') {
            categories[key] = (categories[key] || 0) + 1;
          } else {
            const value = parseFloat(row[validYCol]) || 0;
            categories[key] = (categories[key] || 0) + value;
          }
        });

        const sortedEntries = Object.entries(categories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 15);

        if (sortedEntries.length === 0) return null;

        return {
          series: [{
            name: validYCol === 'count' ? 'Count' : validYCol,
            data: sortedEntries.map(([, value]) => value)
          }],
          options: {
            chart: { 
              type: chart_type, 
              toolbar: { show: true },
              animations: { enabled: true }
            },
            plotOptions: {
              bar: { 
                borderRadius: 8, 
                horizontal: additional_config?.horizontal || false,
                columnWidth: '60%',
                distributed: false
              }
            },
            dataLabels: { enabled: false },
            xaxis: {
              categories: sortedEntries.map(([key]) => key),
              labels: { rotate: -45, style: { fontSize: '11px' } }
            },
            colors: ['#3b82f6'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            tooltip: { theme: 'light' }
          }
        };
      }

      // LINE / AREA CHARTS
      if (chart_type === 'line' || chart_type === 'area') {
        const data = {};
        rows.forEach(row => {
          const key = String(row[validXCol] || 'Unknown');
          const value = parseFloat(row[validYCol]) || 0;
          if (!data[key]) data[key] = [];
          data[key].push(value);
        });

        const avgData = Object.entries(data)
          .map(([key, values]) => ({
            x: key,
            y: values.reduce((a, b) => a + b, 0) / values.length
          }))
          .slice(0, 30);

        if (avgData.length === 0) return null;

        return {
          series: [{ name: validYCol, data: avgData.map(d => d.y) }],
          options: {
            chart: { 
              type: chart_type, 
              toolbar: { show: true }, 
              zoom: { enabled: true },
              animations: { enabled: true }
            },
            stroke: { curve: 'smooth', width: chart_type === 'area' ? 2 : 3 },
            fill: { 
              type: chart_type === 'area' ? 'gradient' : 'solid',
              gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.3
              }
            },
            xaxis: {
              categories: avgData.map(d => d.x),
              labels: { rotate: -45 }
            },
            colors: ['#10b981'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            markers: { size: chart_type === 'line' ? 5 : 0 },
            tooltip: { theme: 'light' }
          }
        };
      }

      // PIE / DONUT CHARTS
      if (chart_type === 'pie' || chart_type === 'donut') {
        const categories = {};
        rows.forEach(row => {
          const key = String(row[validXCol] || 'Unknown');
          categories[key] = (categories[key] || 0) + 1;
        });

        const topCategories = Object.entries(categories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8);

        if (topCategories.length === 0) return null;

        return {
          series: topCategories.map(([, value]) => value),
          options: {
            chart: { type: chart_type, animations: { enabled: true } },
            labels: topCategories.map(([key]) => key),
            colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            legend: { position: 'bottom' },
            dataLabels: { enabled: true },
            tooltip: { theme: 'light' },
            responsive: [{
              breakpoint: 480,
              options: { chart: { width: 300 }, legend: { position: 'bottom' } }
            }]
          }
        };
      }

      // SCATTER / BUBBLE CHARTS
      if (chart_type === 'scatter' || chart_type === 'bubble') {
        const scatterData = rows.slice(0, 100).map(row => {
          const point = {
            x: parseFloat(row[validXCol]) || 0,
            y: parseFloat(row[validYCol]) || 0
          };
          if (chart_type === 'bubble' && z_column) {
            point.z = parseFloat(row[z_column]) || 10;
          }
          return point;
        }).filter(p => !isNaN(p.x) && !isNaN(p.y));

        if (scatterData.length === 0) return null;

        return {
          series: [{ name: `${validXCol} vs ${validYCol}`, data: scatterData }],
          options: {
            chart: { 
              type: chart_type, 
              zoom: { enabled: true, type: 'xy' },
              animations: { enabled: true }
            },
            xaxis: { title: { text: validXCol }, tickAmount: 10 },
            yaxis: { title: { text: validYCol } },
            colors: ['#8b5cf6'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            tooltip: { theme: 'light' }
          }
        };
      }

      // RADIAL BAR CHART
      if (chart_type === 'radialBar') {
        const numericCols = Object.keys(analysisResult.statistics?.numeric_columns || {});
        if (numericCols.length === 0) return null;

        const values = numericCols.slice(0, 5).map(col => {
          const colData = rows.map(r => parseFloat(r[col]) || 0).filter(v => !isNaN(v));
          return colData.reduce((a, b) => a + b, 0) / colData.length;
        });

        const maxVal = Math.max(...values);
        const percentages = values.map(v => Math.round((v / maxVal) * 100));

        return {
          series: percentages,
          options: {
            chart: { type: 'radialBar' },
            plotOptions: {
              radialBar: {
                dataLabels: {
                  name: { fontSize: '14px' },
                  value: { fontSize: '16px' },
                  total: {
                    show: true,
                    label: 'Average',
                    formatter: () => Math.round(values.reduce((a, b) => a + b, 0) / values.length)
                  }
                }
              }
            },
            labels: numericCols.slice(0, 5),
            colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } }
          }
        };
      }

      // HEATMAP CHART
      if (chart_type === 'heatmap') {
        const numericCols = Object.keys(analysisResult.statistics?.numeric_columns || {}).slice(0, 5);
        if (numericCols.length === 0) return null;

        const seriesData = numericCols.map(col => ({
          name: col,
          data: rows.slice(0, 10).map((row, idx) => ({
            x: `Row ${idx + 1}`,
            y: parseFloat(row[col]) || 0
          }))
        }));

        return {
          series: seriesData,
          options: {
            chart: { type: 'heatmap', toolbar: { show: true } },
            dataLabels: { enabled: false },
            colors: ['#3b82f6'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            tooltip: { theme: 'light' }
          }
        };
      }

      // TREEMAP CHART
      if (chart_type === 'treemap') {
        const categories = {};
        rows.forEach(row => {
          const key = String(row[validXCol] || 'Unknown');
          const value = validYCol === 'count' ? 1 : (parseFloat(row[validYCol]) || 1);
          categories[key] = (categories[key] || 0) + value;
        });

        const treemapData = Object.entries(categories)
          .slice(0, 15)
          .map(([key, value]) => ({ x: key, y: value }));

        if (treemapData.length === 0) return null;

        return {
          series: [{ data: treemapData }],
          options: {
            chart: { type: 'treemap', toolbar: { show: true } },
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            colors: ['#3b82f6'],
            plotOptions: {
              treemap: {
                distributed: true,
                enableShades: true
              }
            },
            tooltip: { theme: 'light' }
          }
        };
      }

      // RADAR CHART
      if (chart_type === 'radar') {
        const numericCols = Object.keys(analysisResult.statistics?.numeric_columns || {}).slice(0, 6);
        if (numericCols.length === 0) return null;

        const values = numericCols.map(col => {
          const stats = analysisResult.statistics.numeric_columns[col];
          return stats.mean || 0;
        });

        return {
          series: [{ name: 'Average Values', data: values }],
          options: {
            chart: { type: 'radar', toolbar: { show: true } },
            xaxis: { categories: numericCols },
            colors: ['#8b5cf6'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            tooltip: { theme: 'light' }
          }
        };
      }

      // POLAR AREA CHART
      if (chart_type === 'polarArea') {
        const categories = {};
        rows.forEach(row => {
          const key = String(row[validXCol] || 'Unknown');
          categories[key] = (categories[key] || 0) + 1;
        });

        const topCategories = Object.entries(categories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6);

        if (topCategories.length === 0) return null;

        return {
          series: topCategories.map(([, value]) => value),
          options: {
            chart: { type: 'polarArea' },
            labels: topCategories.map(([key]) => key),
            colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            stroke: { colors: ['#fff'] },
            fill: { opacity: 0.8 },
            tooltip: { theme: 'light' }
          }
        };
      }

      // BOXPLOT CHART
      if (chart_type === 'boxPlot') {
        const numericCols = Object.keys(analysisResult.statistics?.numeric_columns || {}).slice(0, 5);
        if (numericCols.length === 0) return null;

        const boxData = numericCols.map(col => {
          const stats = analysisResult.statistics.numeric_columns[col];
          return {
            x: col,
            y: [stats.min, stats.q25, stats.median, stats.q75, stats.max]
          };
        });

        return {
          series: [{ data: boxData }],
          options: {
            chart: { type: 'boxPlot', toolbar: { show: true } },
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            colors: ['#10b981'],
            tooltip: { theme: 'light' }
          }
        };
      }

      // FUNNEL CHART
      if (chart_type === 'funnel') {
        const categories = {};
        rows.forEach(row => {
          const key = String(row[validXCol] || 'Unknown');
          categories[key] = (categories[key] || 0) + 1;
        });

        const funnelData = Object.entries(categories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6);

        if (funnelData.length === 0) return null;

        return {
          series: [{ name: 'Count', data: funnelData.map(([, value]) => value) }],
          options: {
            chart: { type: 'bar', toolbar: { show: true } },
            plotOptions: {
              bar: {
                borderRadius: 0,
                horizontal: true,
                distributed: true,
                barHeight: '80%',
                isFunnel: true
              }
            },
            dataLabels: {
              enabled: true,
              formatter: (val, opt) => opt.w.globals.labels[opt.dataPointIndex]
            },
            xaxis: { categories: funnelData.map(([key]) => key) },
            colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
            title: { text: title, align: 'left', style: { fontSize: '16px', fontWeight: 600 } },
            legend: { show: false },
            tooltip: { theme: 'light' }
          }
        };
      }

      console.warn('Unsupported chart type:', chart_type);
      return null;
    };

    const chartData = generateChartData();
    
    if (!chartData) {
      return (
        <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
            <div className="text-center">
              <Icon name="info" className="text-gray-400 text-4xl mb-2" />
              <p className="text-gray-500 text-sm">No data available for this chart</p>
              <p className="text-gray-400 text-xs mt-1">Chart type: {chart_type}</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">{chartConfig.description}</p>
        </div>
      );
    }

    return (
      <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <Chart
          options={chartData.options}
          series={chartData.series}
          type={chart_type}
          height={350}
        />
        <p className="mt-4 text-sm text-gray-600">{chartConfig.description}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/super-admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <Icon name="arrow_back" className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Data Analysis</h1>
                <p className="text-sm text-gray-600">AI-powered insights from your data</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Upload Section */}
        {!analysisResult && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="upload_file" className="text-4xl text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Upload Your Data</h2>
              <p className="text-gray-600 mb-6">
                Upload CSV or XLSX files for AI-powered analysis, cleaning, and insights
              </p>

              <div className="max-w-md mx-auto">
                <label className="block">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-3 file:px-6
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                      cursor-pointer"
                  />
                </label>

                {file && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="description" className="text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">{file.name}</span>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Icon name="close" />
                    </button>
                  </div>
                )}

                {error && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-center gap-3">
                    <Icon name="error" className="text-red-600" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}

                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="mt-6 w-full"
                >
                  {uploading ? (
                    <>
                      <Icon name="hourglass_empty" className="animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Icon name="analytics" />
                      Analyze Data
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {analysisResult && (
          <div className="space-y-6">
            {/* Header with Stats */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold">{analysisResult.filename}</h2>
                  <p className="text-blue-100 mt-1">Analysis Complete</p>
                </div>
                <button
                  onClick={() => {
                    setAnalysisResult(null);
                    setFile(null);
                  }}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition flex items-center gap-2"
                >
                  <Icon name="refresh" />
                  New Analysis
                </button>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <Icon name="table_rows" className="text-3xl mb-2" />
                  <p className="text-2xl font-bold">{analysisResult.rows.toLocaleString()}</p>
                  <p className="text-sm text-blue-100">Rows</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <Icon name="view_column" className="text-3xl mb-2" />
                  <p className="text-2xl font-bold">{analysisResult.columns}</p>
                  <p className="text-sm text-blue-100">Columns</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <Icon name="cleaning_services" className="text-3xl mb-2" />
                  <p className="text-2xl font-bold">{analysisResult.cleaning_report.duplicates_removed}</p>
                  <p className="text-sm text-blue-100">Duplicates Removed</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <Icon name="auto_fix_high" className="text-3xl mb-2" />
                  <p className="text-2xl font-bold">{analysisResult.cleaning_report.missing_values_handled}</p>
                  <p className="text-sm text-blue-100">Missing Values Fixed</p>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Key Findings */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Icon name="lightbulb" className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Key Findings</h3>
                </div>
                <ul className="space-y-3">
                  {analysisResult.ai_insights.key_findings.map((finding, idx) => (
                    <li key={idx} className="flex gap-3">
                      <Icon name="check_circle" className="text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{finding}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Insights */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Icon name="insights" className="text-purple-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Insights</h3>
                </div>
                <ul className="space-y-3">
                  {analysisResult.ai_insights.insights.map((insight, idx) => (
                    <li key={idx} className="flex gap-3">
                      <Icon name="trending_up" className="text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Anomalies */}
              {analysisResult.ai_insights.anomalies.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                      <Icon name="warning" className="text-orange-600 text-xl" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Anomalies</h3>
                  </div>
                  <ul className="space-y-3">
                    {analysisResult.ai_insights.anomalies.map((anomaly, idx) => (
                      <li key={idx} className="flex gap-3">
                        <Icon name="error_outline" className="text-orange-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{anomaly}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                    <Icon name="recommend" className="text-green-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Recommendations</h3>
                </div>
                <ul className="space-y-3">
                  {analysisResult.ai_insights.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex gap-3">
                      <Icon name="arrow_forward" className="text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Charts */}
            {analysisResult.ai_insights.chart_recommendations.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Visualizations</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {analysisResult.ai_insights.chart_recommendations
                    .sort((a, b) => a.priority - b.priority)
                    .map((chart, idx) => renderChart(chart, idx))}
                </div>
              </div>
            )}

            {/* Data Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Data Preview</h3>
                <span className="text-sm text-gray-600">
                  Showing {analysisResult.table_data.displayed_rows} of {analysisResult.table_data.total_rows} rows
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {analysisResult.table_data.columns.map((col, idx) => (
                        <th key={idx} className="px-4 py-3 text-left font-semibold text-gray-700">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {analysisResult.table_data.rows.slice(0, 20).map((row, rowIdx) => (
                      <tr key={rowIdx} className="hover:bg-gray-50">
                        {analysisResult.table_data.columns.map((col, colIdx) => (
                          <td key={colIdx} className="px-4 py-3 text-gray-700">
                            {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataAnalysisDashboard;
